import { useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, formatGbp, formatNumber, formatPct } from '../api';
import { usePolling } from '../auth';
import { Badge, Card, Empty, KindBadge, Money, Notice, Percent, Spinner } from '../components/ui';
import type {
  BacktestRunResponse,
  ModelsResponse,
  SavedBacktestRun,
  SignificanceReport,
  StreamKind,
  WalkForwardResult,
} from '../types';

type Toast = (message: string, tone?: 'success' | 'error') => void;

const COLOURS = ['#6ea8fe', '#f7a44c', '#5fd39a', '#c792ea', '#e8c547'];

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Historical simulation.
 *
 * Live, the streams place a handful of trades a day, so telling a real edge from
 * luck would take a year. Replaying stored history produces hundreds of trades in
 * seconds, which is the only practical way to get a statistically meaningful answer.
 */
export default function Backtest({ pushToast }: { pushToast: Toast }) {
  const models = usePolling<ModelsResponse>(() => api.models(), null);
  const runs = usePolling<{ runs: SavedBacktestRun[] }>(() => api.backtestRuns(), null);

  const [from, setFrom] = useState(isoDaysAgo(365));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [funding, setFunding] = useState('10000');
  const [cadence, setCadence] = useState('1');
  const [includeAi, setIncludeAi] = useState(false);
  const [allowShorting, setAllowShorting] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<BacktestRunResponse | null>(null);
  const [walkForward, setWalkForward] = useState<WalkForwardResult | null>(null);

  async function run(): Promise<void> {
    setBusy('backtest');
    setResult(null);
    try {
      const streams: Array<{ name: string; kind: StreamKind; model?: string }> = [
        { name: 'Pattern', kind: 'PATTERN' },
        { name: 'Momentum', kind: 'MOMENTUM' },
        // Trades SPY puts at model prices; needs stored SPY and VIX history.
        { name: 'Options', kind: 'OPTIONS' },
      ];
      if (includeAi && models.data?.configured) {
        const model = models.data.defaultModel;
        streams.push({ name: 'AI', kind: 'AI', model });
        streams.push({ name: 'Hybrid', kind: 'HYBRID', model });
      }

      const response = await api.runBacktest({
        from,
        to,
        streams,
        fundingGbp: Number(funding),
        cadenceDays: Number(cadence),
        risk: { allowShorting },
      });
      setResult(response);
      runs.reload();
      pushToast(
        `Backtest complete: ${response.result.tradingDays} trading days, ${response.result.cyclesRun} cycles.`,
      );
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Backtest failed', 'error');
    } finally {
      setBusy(null);
    }
  }

  async function validate(): Promise<void> {
    setBusy('walk-forward');
    setWalkForward(null);
    try {
      const response = await api.runWalkForward({
        from,
        to,
        folds: 3,
        cadenceDays: Math.max(Number(cadence), 5),
      });
      setWalkForward(response);
      pushToast('Walk-forward validation complete.');
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Validation failed', 'error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Backtest</h1>
          <p>
            Replay the stored daily bars through the same strategies and the same execution code the
            live engine uses. Decisions are computed from bars strictly before each date and filled
            at that day's open, so there is no lookahead.
          </p>
        </div>
      </div>

      <Card title="Configuration">
        <div className="inline-form">
          <div className="field" style={{ maxWidth: 150 }}>
            <label htmlFor="bt-from">From</label>
            <input id="bt-from" type="text" value={from} onChange={(event) => setFrom(event.target.value)} />
          </div>
          <div className="field" style={{ maxWidth: 150 }}>
            <label htmlFor="bt-to">To</label>
            <input id="bt-to" type="text" value={to} onChange={(event) => setTo(event.target.value)} />
          </div>
          <div className="field" style={{ maxWidth: 140 }}>
            <label htmlFor="bt-funding">Funding (GBP)</label>
            <input
              id="bt-funding"
              type="number"
              value={funding}
              onChange={(event) => setFunding(event.target.value)}
            />
          </div>
          <div className="field" style={{ maxWidth: 150 }}>
            <label htmlFor="bt-cadence">Cycle every (days)</label>
            <input
              id="bt-cadence"
              type="number"
              min="1"
              value={cadence}
              onChange={(event) => setCadence(event.target.value)}
            />
            <span className="field-hint">Raise this to cut AI token spend</span>
          </div>
          <div className="field" style={{ maxWidth: 190 }}>
            <label htmlFor="bt-short">Shorting</label>
            <select
              id="bt-short"
              value={allowShorting ? 'yes' : 'no'}
              onChange={(event) => setAllowShorting(event.target.value === 'yes')}
            >
              <option value="yes">Long and short</option>
              <option value="no">Long only</option>
            </select>
          </div>
          <div className="field" style={{ maxWidth: 200 }}>
            <label htmlFor="bt-ai">Streams</label>
            <select
              id="bt-ai"
              value={includeAi ? 'all' : 'pattern'}
              onChange={(event) => setIncludeAi(event.target.value === 'all')}
            >
              <option value="pattern">Pattern, Momentum, Options (free, instant)</option>
              <option value="all">Include AI and Hybrid (uses tokens)</option>
            </select>
          </div>
          <button className="btn btn-primary" type="button" onClick={() => void run()} disabled={busy !== null}>
            {busy === 'backtest' ? <Spinner label="Simulating" /> : 'Run backtest'}
          </button>
          <button className="btn" type="button" onClick={() => void validate()} disabled={busy !== null}>
            {busy === 'walk-forward' ? <Spinner label="Validating" /> : 'Walk-forward validate'}
          </button>
        </div>

        {includeAi && (
          <Notice tone="warn" title="AI backtests call the model once per cycle">
            A year of daily cycles is around 250 model calls per AI stream. The rate limiter paces
            them, so this can take a long time on a free Groq tier. Raise the cadence to sample
            weekly instead.
          </Notice>
        )}
      </Card>

      {result && <BacktestResultView data={result} />}
      {walkForward && <WalkForwardView data={walkForward} />}

      <Card title="Previous runs">
        {runs.data && runs.data.runs.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Period</th>
                  <th className="num">Days</th>
                  <th className="num">Market</th>
                  <th>Streams</th>
                </tr>
              </thead>
              <tbody>
                {runs.data.runs.map((entry) => (
                  <tr key={entry.id}>
                    <td className="mono-small">{new Date(entry.createdAt).toLocaleString('en-GB')}</td>
                    <td className="mono-small">
                      {entry.from} to {entry.to}
                    </td>
                    <td className="num">{entry.summary?.tradingDays ?? '—'}</td>
                    <td className="num">
                      <Percent value={entry.summary?.benchmarkReturnPct ?? null} />
                    </td>
                    <td>
                      {(entry.summary?.streams ?? []).map((stream) => (
                        <span key={stream.name} style={{ marginRight: 12 }}>
                          {stream.name} <Percent value={stream.totalReturnPct} />
                          <span style={{ color: 'var(--text-faint)' }}> ({stream.closedTrades})</span>
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No backtests run yet.</Empty>
        )}
      </Card>
    </div>
  );
}

function BacktestResultView({ data }: { data: BacktestRunResponse }) {
  const { result, significance } = data;
  const market = result.benchmarkReturnPct;

  // Merge the equity curves so every stream plots on one axis.
  const dates = [...new Set(result.streams.flatMap((s) => s.equityCurve.map((p) => p.date)))].sort();
  const merged = dates.map((date) => {
    const row: Record<string, string | number | null> = { date };
    for (const stream of result.streams) {
      row[stream.name] = stream.equityCurve.find((point) => point.date === date)?.equityGbp ?? null;
    }
    return row;
  });

  return (
    <>
      <Card
        title="Results"
        subtitle={`${result.from} to ${result.to} · ${result.tradingDays} trading days · ${result.cyclesRun} cycles · ${(result.durationMs / 1000).toFixed(1)}s`}
      >
        {result.warnings.map((warning) => (
          <Notice key={warning} tone="warn">
            {warning}
          </Notice>
        ))}

        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>Stream</th>
                <th>Approach</th>
                <th className="num">Return</th>
                <th className="num">Alpha</th>
                <th className="num">Final equity</th>
                <th className="num">Trades</th>
                <th className="num">Long/Short</th>
                <th className="num">Win rate</th>
                <th className="num">Profit factor</th>
                <th className="num">Max DD</th>
                <th className="num">Sharpe</th>
                <th className="num">Costs</th>
              </tr>
            </thead>
            <tbody>
              {result.streams.map((stream) => {
                const alpha = market === null ? null : stream.totalReturnPct - market;
                return (
                  <tr key={stream.name}>
                    <td>
                      <strong>{stream.name}</strong>
                      {stream.model && <div className="mono-small">{stream.model}</div>}
                    </td>
                    <td>
                      <KindBadge kind={stream.kind} />
                    </td>
                    <td className="num">
                      <Percent value={stream.totalReturnPct} />
                    </td>
                    <td className="num">
                      <Percent value={alpha} />
                      {alpha !== null && (
                        <div className="mono-small">{alpha > 0 ? 'beat market' : 'lost to market'}</div>
                      )}
                    </td>
                    <td className="num">{formatGbp(stream.endEquityGbp)}</td>
                    <td className="num">{stream.trades.length}</td>
                    <td className="num">
                      {stream.longTrades}/{stream.shortTrades}
                    </td>
                    <td className="num">
                      {stream.winRatePct === null ? '—' : `${stream.winRatePct.toFixed(0)}%`}
                    </td>
                    <td className="num">
                      {stream.profitFactor === null ? '—' : stream.profitFactor.toFixed(2)}
                    </td>
                    <td className="num">{stream.maxDrawdownPct.toFixed(1)}%</td>
                    <td className="num">
                      {stream.sharpeRatio === null ? '—' : stream.sharpeRatio.toFixed(2)}
                    </td>
                    <td className="num">{formatGbp(stream.feesPaidGbp)}</td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={2}>
                  <Badge tone="grey">Market (buy and hold)</Badge>
                </td>
                <td className="num">
                  <Percent value={market} />
                </td>
                <td className="num">—</td>
                <td colSpan={8} style={{ color: 'var(--text-faint)', fontSize: 12 }}>
                  equal-weight, no dealing costs, {result.symbols.length} instruments
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Simulated equity curves">
        <div className="chart-box">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={merged} margin={{ top: 6, right: 12, bottom: 4, left: 4 }}>
              <CartesianGrid stroke="#232c3d" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: '#66748c', fontSize: 11 }} stroke="#33415a" minTickGap={48} />
              <YAxis
                tick={{ fill: '#66748c', fontSize: 11 }}
                stroke="#33415a"
                domain={['auto', 'auto']}
                width={72}
                tickFormatter={(value: number) => `£${Math.round(value).toLocaleString('en-GB')}`}
              />
              <Tooltip
                contentStyle={{
                  background: '#182030',
                  border: '1px solid #33415a',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value) => (typeof value === 'number' ? formatGbp(value) : String(value))}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {result.streams.map((stream, index) => (
                <Line
                  key={stream.name}
                  type="monotone"
                  dataKey={stream.name}
                  stroke={COLOURS[index % COLOURS.length]}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <SignificanceView report={significance} />
    </>
  );
}

export function SignificanceView({ report }: { report: SignificanceReport }) {
  return (
    <Card
      title="Statistical significance"
      subtitle="bootstrap confidence intervals on per-trade P&L"
    >
      <Notice tone={report.pairwise.some((entry) => entry.significant) ? 'info' : 'warn'}>
        {report.summary}
      </Notice>

      <div className="table-wrap" style={{ marginTop: 12 }}>
        <table>
          <thead>
            <tr>
              <th>Stream</th>
              <th className="num">Closed trades</th>
              <th className="num">Mean per trade</th>
              <th>95% confidence interval</th>
              <th>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {report.streams.map((entry) => (
              <tr key={entry.name}>
                <td>
                  <strong>{entry.name}</strong>
                </td>
                <td className="num">{entry.closedTrades}</td>
                <td className="num">
                  <Money value={entry.meanTradeGbp} showSign />
                </td>
                <td className="mono-small">
                  {entry.interval
                    ? `[${formatGbp(entry.interval.lower)}, ${formatGbp(entry.interval.upper)}]`
                    : 'sample too small'}
                </td>
                <td>
                  {entry.interval === null ? (
                    <Badge tone="grey">need more trades</Badge>
                  ) : entry.interval.significant ? (
                    <Badge tone={(entry.meanTradeGbp ?? 0) > 0 ? 'green' : 'red'}>
                      significant
                    </Badge>
                  ) : (
                    <>
                      <Badge tone="amber">inconclusive</Badge>
                      {entry.tradesNeededForSignificance !== null &&
                        entry.tradesNeededForSignificance > 0 && (
                          <div className="mono-small">
                            ~{formatNumber(entry.tradesNeededForSignificance, 0)} more needed
                          </div>
                        )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {report.pairwise.length > 0 && (
        <>
          <h3
            style={{
              marginTop: 18,
              marginBottom: 8,
              fontSize: 12,
              color: 'var(--text-faint)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Head to head
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {report.pairwise.map((comparison) => (
              <div
                key={`${comparison.a}-${comparison.b}`}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 12px',
                  background: 'var(--bg)',
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong>
                    {comparison.a} vs {comparison.b}
                  </strong>
                  <Badge tone={comparison.significant ? 'green' : 'amber'}>
                    {comparison.significant ? 'separated' : 'inconclusive'}
                  </Badge>
                  <span className="mono-small">
                    difference <Money value={comparison.difference} showSign /> per trade, 95% CI [
                    {formatGbp(comparison.lower)}, {formatGbp(comparison.upper)}]
                  </span>
                  <span className="mono-small" style={{ marginLeft: 'auto' }}>
                    P({comparison.a} better) = {(comparison.probabilityABeatsB * 100).toFixed(0)}%
                  </span>
                </div>
                <p className="reason" style={{ margin: '6px 0 0' }}>
                  {comparison.verdict}
                </p>
              </div>
            ))}
          </div>
        </>
      )}

      <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
        A bootstrap is used rather than a t-test because trade P&L is skewed and fat-tailed, so
        assuming a normal distribution would overstate confidence. Intervals are computed from a
        fixed seed, so they do not shift between page loads.
      </p>
    </Card>
  );
}

function WalkForwardView({ data }: { data: WalkForwardResult }) {
  return (
    <Card
      title="Walk-forward validation"
      subtitle="parameters chosen in-sample, then judged once on unseen data"
    >
      <Notice tone={(data.meanOutOfSampleAlphaPct ?? -1) > 0 ? 'info' : 'warn'}>
        {data.verdict}
      </Notice>

      <div className="grid grid-4" style={{ margin: '12px 0' }}>
        <div>
          <div className="stat-label">Mean in-sample</div>
          <div className="stat-value" style={{ fontSize: 18 }}>
            {formatPct(data.meanInSampleReturnPct)}
          </div>
        </div>
        <div>
          <div className="stat-label">Mean out-of-sample</div>
          <div className="stat-value" style={{ fontSize: 18 }}>
            {formatPct(data.meanOutOfSampleReturnPct)}
          </div>
        </div>
        <div>
          <div className="stat-label">Out-of-sample alpha</div>
          <div
            className={`stat-value ${(data.meanOutOfSampleAlphaPct ?? 0) > 0 ? 'pos' : 'neg'}`}
            style={{ fontSize: 18 }}
          >
            {formatPct(data.meanOutOfSampleAlphaPct)}
          </div>
          <div className="stat-foot">the figure that matters</div>
        </div>
        <div>
          <div className="stat-label">Folds beating market</div>
          <div className="stat-value" style={{ fontSize: 18 }}>
            {data.consistencyPct.toFixed(0)}%
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Fold</th>
              <th>In-sample</th>
              <th>Out-of-sample</th>
              <th>Chosen</th>
              <th className="num">In return</th>
              <th className="num">Out return</th>
              <th className="num">Out alpha</th>
              <th className="num">Degradation</th>
            </tr>
          </thead>
          <tbody>
            {data.folds.map((fold) => (
              <tr key={fold.fold}>
                <td>{fold.fold}</td>
                <td className="mono-small">
                  {fold.inSample.from} → {fold.inSample.to}
                </td>
                <td className="mono-small">
                  {fold.outOfSample.from} → {fold.outOfSample.to}
                </td>
                <td>
                  <Badge tone="blue">{fold.chosen.label}</Badge>
                </td>
                <td className="num">
                  <Percent value={fold.inSampleReturnPct} />
                </td>
                <td className="num">
                  <Percent value={fold.outOfSampleReturnPct} />
                </td>
                <td className="num">
                  <Percent value={fold.outOfSampleAlphaPct} />
                </td>
                <td className="num">
                  <Percent value={fold.degradationPct} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
        Success is judged on out-of-sample <strong>alpha</strong>, not raw return. A positive return
        while the market rose is not an edge, since buying anything would have produced one.
      </p>

      {data.warnings.map((warning) => (
        <Notice key={warning} tone="warn">
          {warning}
        </Notice>
      ))}
    </Card>
  );
}
